import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, Plus, X, Star, BadgeCheck, Loader2, Tag as TagIcon } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api, { fmtError, BACKEND_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const MAX_PHOTOS = 9;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const EMOJI_POOL = [
  "💀", "🙄", "🥶", "🤦", "👽", "🔥", "✨", "👻", "🤡", "🥹",
  "🫠", "😎", "🤓", "🥲", "💅", "🌶️", "💯", "🚀", "🦋", "🌸",
];
// Languages: a curated, ordered set so the chips look natural side-by-side.
const LANGUAGE_PRESETS = [
  "english", "swedish", "spanish", "french", "german", "italian", "portuguese",
  "dutch", "arabic", "mandarin", "japanese", "korean", "russian",
  "turkish", "hindi", "polish", "greek",
];
// Interests / tags (no overlap with languages — those live in their own bucket).
const INTEREST_PRESETS = [
  "basketball", "soccer", "tennis", "volleyball", "gym", "yoga", "hiking",
  "running", "cycling", "skating", "surfing", "skiing",
  "tattoo", "music", "art", "photography", "gaming", "cooking", "movies",
  "books", "travel", "tech", "dance", "fashion", "coffee", "cars",
  "animals", "nature", "fitness", "anime", "kpop",
  "lgbtq", "student", "artist", "model", "entrepreneur",
];
// Vibrant Tanta palette for chips (cycles).
const TAG_COLORS = [
  "bg-[#00CED1]",  // yellow
  "bg-[#FF7B6B]",  // coral
  "bg-[#9D5DFF]",  // purple
  "bg-[#22D3EE]",  // cyan
  "bg-[#34D399]",  // green
  "bg-[#FF8AB6]",  // pink
  "bg-[#FB923C]",  // orange
  "bg-[#60A5FA]",  // blue
  "bg-[#F472B6]",  // hot pink
  "bg-[#FBBF24]",  // amber
];
// Language chips lean toward the magenta/purple/cyan side so they read as a group.
const LANG_COLORS = [
  "bg-[#E879F9]",  // magenta
  "bg-[#9D5DFF]",  // purple
  "bg-[#A78BFA]",  // violet
  "bg-[#F472B6]",  // hot pink
  "bg-[#22D3EE]",  // cyan
  "bg-[#60A5FA]",  // blue
];
const EDUCATION_LEVELS = [
  "high_school", "associate", "bachelor", "master", "phd", "self_taught",
];
const PROFESSION_SLOTS = 3;

export default function ProfileEdit() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user, refresh } = useAuth();

  const [photos, setPhotos] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [interests, setInterests] = useState([]);
  const [moodEmojis, setMoodEmojis] = useState([]);
  const [bio, setBio] = useState("");
  const [professions, setProfessions] = useState(["", "", ""]);
  const [educationLevel, setEducationLevel] = useState("");
  const [university, setUniversity] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const fileRef = useRef(null);

  // Hydrate from /auth/me — the AuthContext user may be stale.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (cancelled) return;
        // Migrate: if `photos` is empty but `avatar_url` exists, seed the gallery with it.
        const initial = Array.isArray(data.photos) ? data.photos : [];
        const seeded = initial.length === 0 && data.avatar_url ? [data.avatar_url] : initial;
        setPhotos(seeded.slice(0, MAX_PHOTOS));
        // Migration: extract any language tags that were stored in `interests` and
        // move them to the new `languages` bucket. Old accounts had mixed buckets.
        const rawInterests = data.interests || [];
        const rawLanguages = Array.isArray(data.languages) ? data.languages : [];
        const langSet = new Set(LANGUAGE_PRESETS);
        const migratedLangs = rawInterests.filter((x) => langSet.has(x));
        const cleanedInterests = rawInterests.filter((x) => !langSet.has(x));
        setLanguages([...new Set([...rawLanguages, ...migratedLangs])]);
        setInterests(cleanedInterests);
        setMoodEmojis(data.mood_emojis || []);
        setBio(data.bio || "");
        const profs = Array.isArray(data.professions) ? data.professions : [];
        setProfessions([profs[0] || "", profs[1] || "", profs[2] || ""]);
        setEducationLevel(data.education_level || "");
        setUniversity(data.university || "");
      } catch (e) {
        toast.error(fmtError(e.response?.data?.detail) || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onPickFile = () => fileRef.current?.click();
  const onFile = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) { toast.error(t("profile.imageTooLarge")); return; }
    if (!file.type.startsWith("image/")) { toast.error(t("profile.pickImage")); return; }
    if (photos.length >= MAX_PHOTOS) { toast.error(t("profileEdit.maxPhotos")); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads/media", form, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = data.url.startsWith("http") ? data.url : `${BACKEND_BASE}${data.url}`;
      setPhotos((p) => [...p, fullUrl]);
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || t("profileEdit.uploadFailed"));
    } finally { setUploading(false); }
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));
  const makeMain = (idx) => {
    if (idx === 0) return;
    setPhotos((p) => {
      const next = [...p];
      const [pulled] = next.splice(idx, 1);
      next.unshift(pulled);
      return next;
    });
    toast.success(t("profileEdit.makeMain"));
  };

  const toggleTag = (tag) => setInterests((arr) => arr.includes(tag) ? arr.filter((x) => x !== tag) : [...arr, tag]);
  const toggleLang = (lang) => setLanguages((arr) => arr.includes(lang) ? arr.filter((x) => x !== lang) : [...arr, lang]);
  const toggleEmoji = (e) => setMoodEmojis((arr) => {
    if (arr.includes(e)) return arr.filter((x) => x !== e);
    if (arr.length >= 5) { toast.message(t("profileEdit.emojisHint")); return arr; }
    return [...arr, e];
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me", {
        photos,
        languages,
        interests,
        mood_emojis: moodEmojis,
        bio,
        professions: professions.map((s) => s.trim()).filter(Boolean),
        education_level: educationLevel || null,
        university: university.trim() || null,
      });
      await refresh();
      toast.success(t("profileEdit.saved"));
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || t("profileEdit.saveFailed"));
    } finally { setSaving(false); }
  };

  // Auto-save photos when the array changes (after the first hydration).
  const photosKey = JSON.stringify(photos);
  const didHydrate = useRef(false);
  useEffect(() => {
    if (!didHydrate.current) { didHydrate.current = true; return; }
    api.patch("/users/me", { photos }).then(refresh).catch(() => {});
  }, [photosKey]);

  const isVerified = !!user?.verified;
  const preview = () => nav("/profile");

  const visiblePhotos = showAllPhotos ? photos : photos.slice(0, 4);

  return (
    <div className="min-h-screen bg-white text-[#0A0B0D] max-w-2xl mx-auto pb-32" data-testid="profile-edit-page">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E2DFD4]">
        <div className="flex items-center justify-between px-5 py-4">
          <button onClick={() => nav(-1)} className="w-10 h-10 -ml-2 flex items-center justify-center rounded-full hover:bg-[#F4F1E8]" data-testid="profile-edit-back" aria-label="Back">
            <ArrowLeft className="w-6 h-6" strokeWidth={3} />
          </button>
          <h1 className="font-display font-black text-[26px] tracking-tight">{t("profileEdit.title")}</h1>
          <button
            onClick={preview}
            className="flex items-center gap-1.5 bg-[#00CED1] text-black font-bold text-sm px-4 py-2 rounded-full border-2 border-black shadow-[2px_2px_0_#000] hover:translate-y-[1px] hover:shadow-[1px_1px_0_#000] transition-all"
            data-testid="profile-edit-preview-btn"
          >
            {t("profileEdit.preview")} <Eye className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>
      </div>

      <div className="px-5 pt-6 space-y-8">
        {/* Photos */}
        <section data-testid="photos-section">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-display font-black text-xl">
              {t("profileEdit.photos")} <span className="text-[#7C828D] font-bold">({photos.length})</span>
            </h2>
            {photos.length > 4 && (
              <button onClick={() => setShowAllPhotos((v) => !v)} className="text-sm font-bold bg-[#F4F1E8] hover:bg-[#E9E5D7] px-3 py-1 rounded-full" data-testid="see-all-photos">
                {t("profileEdit.seeAll")}
              </button>
            )}
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
            {/* Add-photo tile */}
            {photos.length < MAX_PHOTOS && (
              <button
                onClick={onPickFile}
                disabled={uploading}
                className="snap-start shrink-0 w-[120px] h-[160px] rounded-2xl bg-[#00CED1] flex items-center justify-center shadow-[3px_3px_0_#000] border-2 border-black hover:translate-y-[1px] hover:shadow-[2px_2px_0_#000] transition-all disabled:opacity-60"
                data-testid="add-photo-btn"
              >
                {uploading
                  ? <Loader2 className="w-8 h-8 animate-spin text-black" />
                  : <div className="w-12 h-12 rounded-full bg-black text-[#00CED1] flex items-center justify-center"><Plus className="w-7 h-7" strokeWidth={3} /></div>}
              </button>
            )}
            <input type="file" accept="image/*" ref={fileRef} onChange={onFile} className="hidden" data-testid="photo-file-input" />
            {visiblePhotos.map((url, i) => (
              <div key={url + i} className="snap-start shrink-0 relative group w-[120px] h-[160px] rounded-2xl overflow-hidden border-2 border-black shadow-[3px_3px_0_#000]" data-testid={`photo-tile-${i}`}>
                <img src={url} alt="" className="w-full h-full object-cover" />
                {i === 0 && (
                  <span className="absolute top-2 left-2 bg-[#00CED1] text-black text-[10px] font-black px-2 py-0.5 rounded-full border border-black">MAIN</span>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end justify-between p-2 opacity-0 group-hover:opacity-100">
                  {i !== 0 && (
                    <button onClick={() => makeMain(i)} className="bg-white text-black rounded-full p-1.5 shadow border border-black" data-testid={`make-main-${i}`} aria-label="Set as main">
                      <Star className="w-3.5 h-3.5" fill="currentColor" />
                    </button>
                  )}
                  <button onClick={() => removePhoto(i)} className="bg-white text-red-600 rounded-full p-1.5 shadow border border-black ml-auto" data-testid={`remove-photo-${i}`} aria-label="Remove">
                    <X className="w-3.5 h-3.5" strokeWidth={3} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Get verified CTA */}
        {!isVerified && (
          <section>
            <h2 className="font-display font-black text-xl mb-3">{t("profileEdit.getVerified")}</h2>
            <button
              onClick={() => nav("/profile?openIdModal=1")}
              className="w-full bg-[#F4F1E8] hover:bg-[#E9E5D7] rounded-2xl px-4 py-4 flex items-center gap-3 text-left transition-colors"
              data-testid="get-verified-btn"
            >
              <span className="w-9 h-9 flex items-center justify-center">
                <BadgeCheck className="w-9 h-9 text-[#00CED1]" strokeWidth={2.5} fill="#00CED1" />
              </span>
              <span className="font-bold text-base">{t("profileEdit.yellowCheckmark")}</span>
            </button>
          </section>
        )}

        {/* Languages */}
        <section data-testid="languages-section">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-display font-black text-xl">
              {t("profileEdit.languages")} <span className="text-[#7C828D] font-bold">({languages.length})</span>
            </h2>
          </div>
          <div className="flex gap-2 flex-wrap mb-3">
            {languages.map((lang, i) => (
              <span
                key={lang}
                className={`inline-flex items-center gap-1 ${LANG_COLORS[i % LANG_COLORS.length]} text-black font-black text-sm px-3 py-1.5 rounded-md uppercase tracking-wide`}
                style={{ transform: `rotate(${(i % 2 === 0 ? -1 : 1) * (1 + (i % 3))}deg)` }}
                data-testid={`active-lang-${lang}`}
              >
                {lang}
                <button onClick={() => toggleLang(lang)} className="ml-1 -mr-1 opacity-80 hover:opacity-100" aria-label="Remove">
                  <X className="w-3 h-3" strokeWidth={4} />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            {LANGUAGE_PRESETS.filter((p) => !languages.includes(p)).map((lang) => (
              <button
                key={lang}
                onClick={() => toggleLang(lang)}
                className="px-3 py-1.5 rounded-md bg-[#F4F1E8] hover:bg-[#E9E5D7] text-sm font-bold uppercase tracking-wide border border-[#D8D3C2]"
                data-testid={`lang-option-${lang}`}
              >
                + {lang}
              </button>
            ))}
          </div>
        </section>

        {/* Tags / Interests */}
        <section data-testid="tags-section">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-display font-black text-xl">{t("profileEdit.tags")}</h2>
          </div>
          <div className="flex gap-2 flex-wrap mb-3">
            <button
              onClick={() => document.getElementById("tag-pool")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center gap-1.5 bg-[#00CED1] text-black font-black text-sm px-3 py-1.5 rounded-full"
              data-testid="add-tags-btn"
            >
              <TagIcon className="w-3.5 h-3.5" strokeWidth={3} /> {t("profileEdit.addTags").toUpperCase()}
            </button>
            {interests.map((tag, i) => (
              <span
                key={tag}
                className={`inline-flex items-center gap-1 ${TAG_COLORS[i % TAG_COLORS.length]} text-black font-black text-sm px-3 py-1.5 rounded-md uppercase tracking-wide`}
                style={{ transform: `rotate(${(i % 2 === 0 ? -1 : 1) * (1 + (i % 3))}deg)` }}
                data-testid={`active-tag-${tag}`}
              >
                {tag}
                <button onClick={() => toggleTag(tag)} className="ml-1 -mr-1 opacity-80 hover:opacity-100" aria-label="Remove">
                  <X className="w-3 h-3" strokeWidth={4} />
                </button>
              </span>
            ))}
          </div>
          <div id="tag-pool" className="flex gap-2 flex-wrap">
            {INTEREST_PRESETS.filter((p) => !interests.includes(p)).map((tag) => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className="px-3 py-1.5 rounded-md bg-[#F4F1E8] hover:bg-[#E9E5D7] text-sm font-bold uppercase tracking-wide border border-[#D8D3C2]"
                data-testid={`tag-option-${tag}`}
              >
                + {tag}
              </button>
            ))}
          </div>
        </section>

        {/* Emojis */}
        <section data-testid="emojis-section">
          <h2 className="font-display font-black text-xl mb-1">{t("profileEdit.emojis")}</h2>
          <p className="text-xs text-[#7C828D] mb-3">{t("profileEdit.emojisHint")} · {moodEmojis.length}/5</p>
          <div className="flex gap-2 flex-wrap">
            {EMOJI_POOL.map((e) => {
              const active = moodEmojis.includes(e);
              return (
                <button key={e} onClick={() => toggleEmoji(e)}
                  className={`w-12 h-12 flex items-center justify-center text-2xl rounded-xl transition-all ${active ? "bg-[#00CED1] scale-110 shadow-[2px_2px_0_#000] border-2 border-black" : "bg-[#F4F1E8] hover:bg-[#E9E5D7]"}`}
                  data-testid={`emoji-${e}`}
                >
                  {e}
                </button>
              );
            })}
          </div>
        </section>

        {/* Biography */}
        <section data-testid="bio-section">
          <h2 className="font-display font-black text-xl mb-3">{t("profileEdit.biography")}</h2>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, 500))}
            placeholder={t("profileEdit.describeYourself")}
            rows={4}
            className="w-full bg-[#F4F1E8] rounded-2xl px-4 py-3 text-base placeholder-[#7C828D] outline-none focus:bg-[#E9E5D7] resize-none"
            data-testid="bio-textarea"
          />
          <div className="text-xs text-[#7C828D] text-right mt-1">{bio.length}/500</div>
        </section>

        {/* Profession */}
        <section data-testid="profession-section">
          <h2 className="font-display font-black text-xl mb-3">{t("profileEdit.profession")}</h2>
          <div className="space-y-2">
            {Array.from({ length: PROFESSION_SLOTS }).map((_, i) => (
              <input
                key={i}
                type="text"
                value={professions[i] || ""}
                onChange={(e) => setProfessions((arr) => { const next = [...arr]; next[i] = e.target.value.slice(0, 120); return next; })}
                placeholder={t("profileEdit.professionPlaceholder")}
                className="w-full bg-[#F4F1E8] rounded-2xl px-4 py-3 text-base placeholder-[#7C828D] outline-none focus:bg-[#E9E5D7]"
                data-testid={`profession-input-${i}`}
              />
            ))}
          </div>
        </section>

        {/* Education */}
        <section data-testid="education-section">
          <h2 className="font-display font-black text-xl mb-3">{t("profileEdit.education")}</h2>
          <div className="space-y-2">
            <select
              value={educationLevel}
              onChange={(e) => setEducationLevel(e.target.value)}
              className="w-full bg-[#F4F1E8] rounded-2xl px-4 py-3 text-base outline-none focus:bg-[#E9E5D7] appearance-none cursor-pointer"
              data-testid="education-level-select"
            >
              <option value="">{t("profileEdit.educationLevel")}</option>
              {EDUCATION_LEVELS.map((lvl) => (
                <option key={lvl} value={lvl}>{lvl.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}</option>
              ))}
            </select>
            <input
              type="text"
              value={university}
              onChange={(e) => setUniversity(e.target.value.slice(0, 120))}
              placeholder={t("profileEdit.university")}
              className="w-full bg-[#F4F1E8] rounded-2xl px-4 py-3 text-base placeholder-[#7C828D] outline-none focus:bg-[#E9E5D7]"
              data-testid="university-input"
            />
          </div>
        </section>

        {/* Save bar */}
        <div className="sticky bottom-4 z-20 pt-2">
          <button
            onClick={save}
            disabled={saving}
            className="w-full bg-black text-[#00CED1] font-black text-base py-4 rounded-2xl shadow-[4px_4px_0_#00CED1] border-2 border-black hover:translate-y-[1px] hover:shadow-[2px_2px_0_#00CED1] transition-all disabled:opacity-60"
            data-testid="profile-edit-save-btn"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin inline-block" /> : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
